import { useEffect, useRef, useState } from "react";
import styles from "../index.module.scss";
import LinkImg from "./LinkImg.svg";
import DownArrow from "./DownArrow.svg";
import Clicked from "./Clicked.svg";
import DragConfig from "./DragConfig";
import Arrow from "./Arrow.svg";
import AddIcon from "./AddIcon.svg";
import Modal from "./Modal";
import QuestionConfig from "./QuestionConfig";
import axios from "axios";
import UrlLinks from "../../../../UrlLinks";

export default function ConfigForm({
  title = "",
  stage = 0,
  subtitle = "",
  availAttr,
  setAvailAttr,
  isEnabled = false,
  setIsEnabled,
  selectedAttr = [],
  setSelectedAttr,
  questions,
  setQuestions,
  totalMarks,
  setTotalMarks,
  passingMarks,
  setPassingMarks,
  reapply,
  setReapply,
  // Training specific props
  selectedTrainingModule,
  setSelectedTrainingModule,
  trainingModuleId,
  setTrainingModuleId,
}) {
  // Function to format module type for display
  const formatModuleTypeForDisplay = (moduleType) => {
    if (moduleType === "ASSESSMENT") return "Assessment";
    if (moduleType === "COURSE") return "Course";
    if (moduleType === "PLAYLIST") return "Playlist";
    return moduleType; // fallback to original if not found
  };
  const containerRef = useRef(null);
  const [isDropDwnOpen, setIsDropDwnOpen] = useState(false);
  const [tempAttr, setTempAttr] = useState([]);
  const [questionModal, setQuestionModal] = useState(false);

  const initialOptions = [
    { option_id: "temp-00", option_text: "" },
    { option_id: "temp-01", option_text: "" },
  ];

  const [question, setQuestion] = useState({
    questionId: `temp-${Date.now()}`,
    questionText: "",
    answerList: initialOptions,
    answer: "",
    answerId: "",
    marks: 0,
  });

  const handleDelete = async (questionId, questionMarks) => {
    const isTemporary =
      typeof questionId === "string" && questionId.startsWith("temp");

    if (!isTemporary) {
      const userId = localStorage.getItem("userID");
      try {
        await axios.post(
          `${UrlLinks.baseUrl}task-job/deleteScreeningQuestion?insertedBy=${userId}&questionId=${questionId}`
        );
      } catch (error) {
        console.log("Error deleting question:", error);
      }
    }

    setQuestions((prevQuestionArray) =>
      prevQuestionArray.filter((question) => question.questionId !== questionId)
    );
    setTotalMarks((prev) => prev - questionMarks);
  };

  const clearAll = async () => {
    await Promise.all(
      questions.map((q) => handleDelete(q.questionId, q.marks))
    );
  };

  const handleEdit = (item) => {
    setQuestionModal(true);
    setQuestion(item);
  };

  const handleClose = () => {
    setQuestionModal(false);
    setQuestion({
      questionId: `temp-${Date.now()}`,
      questionText: "",
      answerList: initialOptions,
      answer: initialOptions[0].option_text,
      answerId: initialOptions[0].option_id,
      marks: 0,
    });
  };

  const toggleSwitch = () => {
    if (stage === 4) {
      setIsEnabled((prev) => !prev);
      return;
    }
    if (stage !== 1 && stage !== 3) {
      setIsEnabled((prev) => !prev);
      return;
    }
    const hasMandates = selectedAttr.some((attr) => attr.mandate);

    if (hasMandates) {
      return;
    }

    setIsEnabled((prev) => !prev);

    if (tempAttr.length) setTempAttr([]);

    if (selectedAttr.length) {
      const nonMandates = selectedAttr.filter((attr) => !attr.mandate);
      const nonMandateAttributes = nonMandates.map((attr) => attr.attribute);

      setAvailAttr((prev) => [...prev, ...nonMandateAttributes]);

      setSelectedAttr((prev) => prev.filter((attr) => attr.mandate));
    }

    setIsDropDwnOpen(false);
  };

  const HierarchyMap = () => {
    return (
      <div className={styles.HierarchyMap}>
        <div className={styles.HierarchyMapTitle}>Hierarchy Preview:</div>
        <div className={styles.HierarchyMapRow}>
          {selectedAttr.map((i, index) => (
            <div key={index} className={styles.HierarchyItem}>
              <div className={styles.HierarchyItemTitle}>
                <div>{index + 1}.</div>
                {i.attribute}
              </div>
              {index !== selectedAttr.length - 1 && (
                <img width={10} height={10} src={Arrow} alt="arrow" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleCopy = async (question) => {
    const newQuestion = {
      ...question,
      questionId: `temp-${Date.now()}`, // Unique temporary ID
    };
    setQuestions((prev) => [...prev, newQuestion]);
    setTotalMarks((prev) => prev + question.marks);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsDropDwnOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (selectedAttr.length && isEnabled) {
      setAvailAttr((prev) =>
        prev.filter((i) => !selectedAttr.some((attr) => attr.attribute === i))
      );
    }
  }, [selectedAttr, isEnabled]);

  return (
    <section className={styles.configForm}>
      <Modal isOpen={questionModal} onClose={() => setQuestionModal(false)}>
        <QuestionConfig
          setTotalMarks={setTotalMarks}
          initialOptions={initialOptions}
          setQuestions={setQuestions}
          questions={questions}
          question={question}
          setQuestion={setQuestion}
          close={
            question.questionText === ""
              ? () => setQuestionModal(false)
              : handleClose
          }
        />
      </Modal>
      <section
        className={`${styles.headers} ${!isEnabled ? styles.opacityLow : ""}`}
      >
        <section className={styles.titlebar}>
          <div className={styles.title}>
            <div className={styles.titleLine}>{title}</div>
            <div className={styles.stage}>Stage {stage}</div>
          </div>
          <section className={styles.toggleBar} onClick={toggleSwitch}>
            <div
              className={`${styles.toggleStatus} ${
                isEnabled ? styles.enabled : styles.disabled
              }`}
            >
              {isEnabled ? "Enabled" : "Disabled"}
            </div>
            <div
              className={`${styles.toggleSwitch} ${
                isEnabled ? styles.enabled : styles.disabled
              }`}
            >
              <div className={styles.toggleThumb}></div>
            </div>
          </section>
        </section>
        <div className={styles.subtitle}>{subtitle}</div>
      </section>
      {(stage === 4 || stage === 1 || stage === 3) && (
        <section
          className={`${styles.trainingForm} ${
            !isEnabled ? styles.opacityLow : ""
          }`}
        >
          <div className={styles.head}>
            {stage === 1
              ? "Add Questions"
              : stage === 3
              ? "Add Documents"
              : stage === 4
              ? (
                <span>
                  Add Training Module{isEnabled && (
                    <span style={{ color: "#D32F2F" }}> *</span>
                  )}
                </span>
              )
              : "Training Link"}
          </div>
          {stage === 4 && (
            <div className={styles.attrCont}>
              <div className={styles.attributes}>
                <div
                  onClick={() => {
                    if (isEnabled) setIsDropDwnOpen((prev) => !prev);
                    else return;
                  }}
                  ref={containerRef}
                  className={styles.attribute}
                >
                  <input
                    className={`${styles.attributeInp} ${
                      !isEnabled ? styles.opacityLow : ""
                    }`}
                    value={selectedTrainingModule ? formatModuleTypeForDisplay(selectedTrainingModule.moduleType) : ""}
                    readOnly
                    placeholder="Select training module"
                  />

                  <img
                    className={`${styles.attributeDownArrow} ${
                      isDropDwnOpen && isEnabled ? styles.attributeUp : ""
                    } ${!isEnabled ? styles.opacityLow : ""}`}
                    src={DownArrow}
                    alt="img"
                  />
                  {isDropDwnOpen && isEnabled && (
                    <div className={styles.attributeDropDown}>
                      {availAttr.length ? (
                        availAttr.map((module, index) => (
                            <div
                              onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTrainingModule(module);
                              setIsDropDwnOpen(false);
                            }}
                            className={styles.drpDwnItem}
                            key={index}
                          >
                            <div className={styles.radioButton}>
                              <div className={`${styles.radioButtonInner} ${
                                selectedTrainingModule && selectedTrainingModule.moduleType === module.moduleType
                                  ? styles.radioButtonSelected
                                  : ''
                              }`}>
                                {selectedTrainingModule && selectedTrainingModule.moduleType === module.moduleType && (
                                  <div className={styles.radioButtonDot}></div>
                                )}
                              </div>
                            </div>
                            {formatModuleTypeForDisplay(module.moduleType)}
                          </div>
                        ))
                      ) : (
                        <div className={styles.noAttr}>
                          No Training Modules Available
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.attributeLine}></div>
              
              {/* Module ID Input */}
              <div className={styles.trainingModuleIdContainer}>
                <div className={styles.selectedAttrHead}>Module ID {isEnabled && (<span style={{ color: "#D32F2F" }}>*</span>)}</div>
                <input
                  type="number"
                  min="1"
                  step="1"
                  className={`${styles.trainingModuleIdInput} ${
                    !isEnabled ? styles.opacityLow : ""
                  }`}
                  value={trainingModuleId}
                  onChange={(e) => {
                    const value = e.target.value;
                    // Only allow positive integers
                    if (value === "" || (parseInt(value) > 0 && Number.isInteger(parseFloat(value)))) {
                      setTrainingModuleId(value);
                    }
                  }}
                  placeholder="Enter module ID"
                  disabled={!isEnabled}
                />
              </div>
            </div>
          )}
            
          {(stage === 1 || stage === 3) && (
            <div className={styles.attrCont}>
              <div className={styles.attributes}>
                <div
                  onClick={() => {
                    if (isEnabled) setIsDropDwnOpen((prev) => !prev);
                    else return;
                  }}
                  ref={containerRef}
                  className={styles.attribute}
                >
                  <input
                    className={`${styles.attributeInp} ${
                      !isEnabled ? styles.opacityLow : ""
                    }`}
                    value={tempAttr.length > 0 ? tempAttr.join(", ") : ""}
                    readOnly
                    placeholder={
                      stage === 1
                        ? "Select a question to add"
                        : "Select a Document to add"
                    }
                  />

                  <img
                    className={`${styles.attributeDownArrow} ${
                      isDropDwnOpen && isEnabled ? styles.attributeUp : ""
                    } ${!isEnabled ? styles.opacityLow : ""}`}
                    src={DownArrow}
                    alt="img"
                  />
                  {isDropDwnOpen && isEnabled && (
                    <div className={styles.attributeDropDown}>
                      {availAttr.length ? (
                        availAttr.map((i, index) => (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              if (tempAttr.includes(i))
                                setTempAttr((prev) =>
                                  prev.filter((k) => k !== i)
                                );
                              else setTempAttr((prev) => [...prev, i]);
                            }}
                            className={styles.drpDwnItem}
                            key={index}
                          >
                            <div className={styles.drpItemBox}>
                              {tempAttr.includes(i) && (
                                <img src={Clicked} alt="image" />
                              )}
                            </div>
                            {i}
                          </div>
                        ))
                      ) : (
                        <div className={styles.noAttr}>
                          No Attribute Available
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div
                  onClick={() => {
                    if (isEnabled && tempAttr.length) {
                      setSelectedAttr((prev) => [
                        ...prev,
                        ...tempAttr.map((i) => ({
                          attribute: i,
                          mandate: false,
                        })),
                      ]);
                      setTempAttr([]);
                    }
                  }}
                  className={`${styles.addAttribute} ${
                    tempAttr.length && isEnabled ? styles.addAttrActive : ""
                  }`}
                >
                  Add &#40;{tempAttr.length}&#41;
                </div>
              </div>
              <div className={styles.attributeLine}></div>
              <div className={styles.selectedAttrHead}>{`Selected ${
                stage === 1 ? "Questions" : "Attributes"
              } (${selectedAttr.length})`}</div>
            </div>
          )}

          {(stage === 1 || stage === 3) && (
            <div className={styles.dragAttrSection}>
              {!isEnabled || !selectedAttr.length ? (
                <div className={styles.dragDisabledAttr}>
                  <div className={styles.dragDisabledAttrTexts}>
                    <div className={styles.dragDisabledAttrText}>
                      No attributes selected yet
                    </div>
                    <div className={styles.dragDisabledAttrSubText}>
                      Add attributes above to configure this stage
                    </div>
                  </div>
                </div>
              ) : (
                <DragConfig
                  containerArray={selectedAttr}
                  setContainerArray={setSelectedAttr}
                  setAvailAttr={setAvailAttr}
                  stage={stage}
                />
              )}
            </div>
          )}
          {selectedAttr.length && stage === 1 && isEnabled ? (
            <HierarchyMap />
          ) : null}
        </section>
      )}
      {stage === 2 && isEnabled ? (
        <section className={styles.screeningContainer}>
          <div className={styles.screeningHead}>
            <div className={styles.screeningTitle}>Screening Questions</div>
            <div className={styles.screeningHeadLeft}>
              <div
                onClick={() => {
                  setQuestionModal(true);
                  setQuestion({
                    questionId: `temp-${Date.now()}`,
                    questionText: "",
                    answerList: initialOptions,
                    answer: initialOptions[0].option_text,
                    answerId: initialOptions[0].option_id,
                    marks: 0,
                  });
                }}
                className={styles.screeningAddQues}
              >
                <img src={AddIcon} alt="Add" />
                <div>Add Questions</div>
              </div>
            </div>
          </div>
          <div className={styles.screeningInfo}>
            <div className={styles.screeningQuesInfo}>
              <div className={styles.screeningInfoHead}>Total Questions</div>
              <div className={styles.screeningInfoData}>{questions.length}</div>
            </div>
            <div className={styles.screeningQuesInfo}>
              <div className={styles.screeningInfoHead}>Total Score</div>
              <div className={styles.screeningInfoData}>{totalMarks} marks</div>
            </div>
          </div>
          <div className={styles.screeningPassMarksInfo}>
            <div className={styles.screeningPassMarksTitle}>
              Passing Marks :{" "}
            </div>
            <input
              value={passingMarks}
              onChange={(e) => {
                const val = e.target.value;

                if (val === "") {
                  setPassingMarks("");
                  return;
                }

                const numberOnlyRegex = /^\d+$/;
                if (!numberOnlyRegex.test(val)) return;

                const num = Number(val);
                setPassingMarks(num); // allow the number to be set
              }}
              className={`${styles.screeningPassMarksView} ${
                passingMarks > totalMarks ? styles.invalid : ""
              }`}
            />

            <div className={styles.screeningTotalMarksView}>
              Out of {totalMarks}
            </div>
          </div>
          <div className={styles.attributeLineScreening}></div>
          <div className={styles.screeningQuesHead}>
            {`Question (${questions.length})`}

            {questions.length ? (
              <div onClick={clearAll} className={styles.screeningQuesHeadClear}>
                Clear All
              </div>
            ) : null}
          </div>
          <div className={styles.dragQuesSection}>
            {questions.length === 0 ? (
              <div className={styles.dragDisabledQues}>
                <div className={styles.dragDisabledQuesTexts}>
                  <div className={styles.dragDisabledQuesText}>
                    No attributes selected yet
                  </div>
                  <div className={styles.dragDisabledQuesSubText}>
                    Add attributes above to configure this stage
                  </div>
                </div>
              </div>
            ) : (
              <DragConfig
                isQuestion={true}
                containerArray={questions}
                setContainerArray={setQuestions}
                handleDelete={handleDelete}
                handleCopy={handleCopy}
                handleEdit={handleEdit}
              />
            )}
          </div>
        </section>
      ) : stage === 2 ? (
        <div className={styles.screeningDragDisable}>
          <div className={styles.screeningDragDisableText}>
            This stage is disabled
          </div>
          <div className={styles.screeningDragDisableSubText}>
            Enable the stage to configure its attributes.
          </div>
        </div>
      ) : null}
    </section>
  );
}
